/**
 * Audio Player Utility
 * Reproduz chunks de áudio PCM 24kHz em tempo real
 *
 * iOS Silent Mode Bypass:
 * Uses HTML5 Audio element with silent audio to force the audio session
 * into "playback" category, which ignores the silent mode switch.
 */

import { logger } from "./logger";

// Silent MP3 data URL - 1 second of silence
// This is used to trick iOS into using "playback" audio category
const SILENT_MP3_DATA_URL =
  "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNBrv2AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNBrv2AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";

export class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private nextStartTime = 0;
  private readonly sampleRate = 24000; // Azure OpenAI Realtime uses 24kHz
  private isUnlocked = false;
  private silentAudioElement: HTMLAudioElement | null = null;
  private isSilentModeBypassActive = false;

  constructor() {}

  private ensureContext(): void {
    if (!this.audioContext) {
      // iOS Safari does not support forcing a specific sample rate in AudioContext
      // We must let the browser choose the native sample rate (usually 44.1kHz or 48kHz)
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;

      // Remove latencyHint as it can cause issues on some iOS versions
      // and let the browser decide the best configuration
      this.audioContext = new AudioContextClass();

      logger.log(
        `[AudioPlayer] 🔊 Created AudioContext (impl: ${
          window.AudioContext ? "standard" : "webkit"
        })`
      );

      this.nextStartTime = this.audioContext.currentTime;
    }
  }

  /**
   * Resample audio data to match the AudioContext's sample rate
   * This prevents iOS playback issues where the browser fails to resample small chunks correctly
   */
  private resample(data: Float32Array, targetRate: number): Float32Array {
    if (this.sampleRate === targetRate) return data;

    const ratio = this.sampleRate / targetRate;
    const newLength = Math.round(data.length / ratio);
    const result = new Float32Array(newLength);

    for (let i = 0; i < newLength; i++) {
      const position = i * ratio;
      const index = Math.floor(position);
      const fraction = position - index;

      if (index >= data.length - 1) {
        result[i] = data[data.length - 1];
      } else {
        // Linear interpolation
        result[i] = data[index] * (1 - fraction) + data[index + 1] * fraction;
      }
    }

    return result;
  }

  /**
   * Ensure the AudioContext is running (helps bypass autoplay policies)
   * Also plays a silent buffer to unlock iOS audio engine
   */
  async resume(): Promise<void> {
    this.ensureContext();

    if (!this.audioContext) return;

    // CRITICAL: Start silent mode bypass FIRST (iOS)
    // This forces the audio session into "playback" category
    await this.startSilentModeBypass();

    // Always try to resume if not running
    if (this.audioContext.state !== "running") {
      try {
        await this.audioContext.resume();
        logger.log(
          `[AudioPlayer] ▶️ Resumed AudioContext (state: ${this.audioContext.state})`
        );
      } catch (e) {
        logger.warn("[AudioPlayer] ⚠️ Failed to resume AudioContext:", e);
      }
    }

    // iOS Hack: Play a short silent buffer to fully unlock the audio engine
    // Only do this once per session to avoid spamming the audio thread
    if (!this.isUnlocked) {
      try {
        // Create a short silent buffer (e.g. 10ms) instead of just 1 sample
        // This is more reliable on some iOS versions
        const buffer = this.audioContext.createBuffer(
          1,
          Math.ceil(this.audioContext.sampleRate / 100), // 10ms
          this.audioContext.sampleRate
        );
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.start(0);
        this.isUnlocked = true;
        logger.log(
          `[AudioPlayer] 🔇 Played silent buffer to unlock iOS audio (rate: ${this.audioContext.sampleRate}Hz)`
        );
      } catch (e) {
        logger.warn("[AudioPlayer] ⚠️ Failed to play silent buffer:", e);
      }
    } else {
      // logger.log("[AudioPlayer] ⏭️ Silent buffer already played, skipping");
    }
  }

  /**
   * Start the silent mode bypass for iOS
   * Uses an HTML5 Audio element playing silent audio to force the system
   * into "playback" audio category, which bypasses the silent mode switch
   */
  private async startSilentModeBypass(): Promise<void> {
    // Only needed on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    // Already active
    if (this.isSilentModeBypassActive && this.silentAudioElement) return;

    try {
      if (!this.silentAudioElement) {
        this.silentAudioElement = new Audio();
        this.silentAudioElement.src = SILENT_MP3_DATA_URL;
        this.silentAudioElement.loop = true;
        // Use a very low but non-zero volume - iOS needs this to activate the audio session
        // Volume of 0 might not trigger the session change on some iOS versions
        this.silentAudioElement.volume = 0.001;
        // CRITICAL: Set playsinline to prevent iOS from opening fullscreen
        this.silentAudioElement.setAttribute("playsinline", "true");
        this.silentAudioElement.setAttribute("webkit-playsinline", "true");
      }

      await this.silentAudioElement.play();
      this.isSilentModeBypassActive = true;
      logger.log(
        "[AudioPlayer] 🔊 Silent mode bypass ACTIVE - audio will play even with ringer off"
      );
    } catch (e) {
      logger.warn("[AudioPlayer] ⚠️ Failed to start silent mode bypass:", e);
    }
  }

  /**
   * Stop the silent mode bypass
   */
  private stopSilentModeBypass(): void {
    if (this.silentAudioElement) {
      this.silentAudioElement.pause();
      this.silentAudioElement.src = "";
      this.silentAudioElement = null;
    }
    this.isSilentModeBypassActive = false;
    logger.log("[AudioPlayer] 🔇 Silent mode bypass stopped");
  }

  /**
   * Play PCM audio chunk (Int16Array or Float32Array)
   * Returns a Promise that resolves when audio finishes playing
   */
  async playChunk(pcmData: Int16Array | Float32Array): Promise<void> {
    this.ensureContext();

    if (!this.audioContext) {
      throw new Error("AudioContext not initialized");
    }

    await this.resume().catch((error) => {
      logger.warn("[AudioPlayer] ⚠️ Failed to resume before playback:", error);
    });

    logger.log("[AudioPlayer] 🎵 playChunk called:", {
      dataType: pcmData.constructor.name,
      samples: pcmData.length,
      duration: `${Math.round((pcmData.length / this.sampleRate) * 1000)}ms`,
      contextState: this.audioContext.state,
      currentTime: this.audioContext.currentTime.toFixed(3),
      nextStartTime: this.nextStartTime.toFixed(3),
    });

    // Resume AudioContext if suspended (browser policy)
    if (this.audioContext.state === "suspended") {
      logger.log("[AudioPlayer] ▶️ Resuming suspended AudioContext...");
      await this.audioContext.resume();
      logger.log("[AudioPlayer] ✅ AudioContext resumed");
    }

    // Convert Int16 to Float32 if needed
    let float32Data: Float32Array;
    if (pcmData instanceof Int16Array) {
      float32Data = new Float32Array(pcmData.length);
      for (let i = 0; i < pcmData.length; i++) {
        float32Data[i] = pcmData[i] / (pcmData[i] < 0 ? 0x8000 : 0x7fff);
      }
    } else {
      float32Data = pcmData;
    }

    // Resample if needed (CRITICAL for iOS)
    // iOS often fails to play 24kHz buffers on 48kHz context without artifacts or silence
    const targetRate = this.audioContext.sampleRate;
    if (targetRate !== this.sampleRate) {
      logger.log(
        `[AudioPlayer] 🔄 Resampling ${float32Data.length} samples from ${this.sampleRate}Hz to ${targetRate}Hz`
      );
      float32Data = this.resample(float32Data, targetRate);
    }

    // Create AudioBuffer with the CONTEXT'S sample rate
    const audioBuffer = this.audioContext.createBuffer(
      1, // mono
      float32Data.length,
      targetRate
    );

    // Ensure Float32Array has ArrayBuffer type
    const channelData = audioBuffer.getChannelData(0);
    channelData.set(float32Data);

    // Create source and gain node
    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();

    source.buffer = audioBuffer;
    gainNode.gain.value = 1.0; // Explicitly set volume

    // Connect graph: Source -> Gain -> Destination
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Schedule playback
    const currentTime = this.audioContext.currentTime;

    // If nextStartTime is in the past (lag), reset it to now
    if (this.nextStartTime < currentTime) {
      this.nextStartTime = currentTime;
    }

    // Add small safety buffer (10ms) to prevent scheduling in the past due to processing time
    const startTime = Math.max(this.nextStartTime, currentTime + 0.01);

    // Update next start time BEFORE starting playback
    this.nextStartTime = startTime + audioBuffer.duration;

    // Return promise that resolves when audio actually finishes
    return new Promise<void>((resolve) => {
      source.onended = () => {
        // Cleanup nodes to prevent memory leaks
        source.disconnect();
        gainNode.disconnect();
        resolve();
      };
      source.start(startTime);
    });
  }

  /**
   * Stop all audio and reset
   */
  stop(): void {
    if (this.audioContext) {
      this.nextStartTime = this.audioContext.currentTime;
    }
  }

  /**
   * Force audio routing to speaker on iOS
   * Re-activates the silent mode bypass to ensure audio plays through speaker
   */
  async resetRouting(): Promise<void> {
    // Re-initialize the silent mode bypass
    // This ensures the audio session stays in "playback" category
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      logger.log("[AudioPlayer] 🔄 Resetting audio routing for iOS...");

      // Stop and restart the bypass to refresh the audio session
      if (this.silentAudioElement) {
        try {
          // Just ensure it's playing - no need to stop/restart
          if (this.silentAudioElement.paused) {
            await this.silentAudioElement.play();
            logger.log(
              "[AudioPlayer] ▶️ Silent audio resumed for routing reset"
            );
          }
        } catch (e) {
          logger.warn("[AudioPlayer] ⚠️ Failed to resume silent audio:", e);
          // Try to recreate it
          await this.startSilentModeBypass();
        }
      } else {
        // Create new bypass if it doesn't exist
        await this.startSilentModeBypass();
      }
    }
  }

  /**
   * Close audio context and cleanup
   */
  async close(): Promise<void> {
    // Stop the silent mode bypass first
    this.stopSilentModeBypass();

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    this.isUnlocked = false;
    this.isSilentModeBypassActive = false;
  }

  /**
   * Get current playback state
   */
  isPlaying(): boolean {
    if (!this.audioContext) return false;
    return this.nextStartTime > this.audioContext.currentTime;
  }

  /**
   * Get remaining playback time in milliseconds
   */
  getRemainingPlaybackTime(): number {
    if (!this.audioContext) return 0;

    const remaining = this.nextStartTime - this.audioContext.currentTime;
    return Math.max(0, remaining * 1000); // Convert to milliseconds
  }

  getState(): AudioContextState | "closed" {
    if (!this.audioContext) {
      return "closed";
    }

    return this.audioContext.state;
  }
}
